import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import { AttachmentModal } from './AttachmentModal'
import { fullPreviewKindOf } from './previewKind'
import type { PreviewItem } from './types'

/**
 * Konusma GENELINDE onizleme galerisi.
 *
 * Onceden modal'a yalnizca TIKLANAN mesajin ekleri veriliyordu; tek ekli bir
 * mesajda gezinme dugmeleri hic gorunmuyordu (kullanici geri bildirimi:
 * "sag-sol kaydirma da olmali"). Artik konusmadaki tum ekler - kullanicinin
 * yukledikleri ve araclarin urettikleri - mesaj sirasiyla TEK bir listede;
 * hangi eke tiklanirsa modal o ogeden aciliyor.
 *
 * Saglayici YOKSA (ornegin paylasim ekrani) cagiran bilesen kendi yerel
 * modal'ina dusuyor - bu yuzden `openById` basarili olup olmadigini donduruyor.
 */

export interface GalleryFile {
  id: string
  filename: string
  mime: string
  byte_size?: number
  width?: number | null
  height?: number | null
  kind?: 'image' | 'video' | 'audio' | 'document' | 'text'
}

export function toPreviewItem(file: GalleryFile, { foreign = false } = {}): PreviewItem {
  const kind = file.kind ?? (file.mime.startsWith('image/') ? 'image' : 'document')
  const fullPreviewKind = fullPreviewKindOf(file.filename, file.mime)
  return {
    kind,
    filename: file.filename,
    mime: file.mime,
    byteSize: file.byte_size ?? 0,
    previewUrl: `/api/attachments/${file.id}`,
    downloadUrl: `/api/attachments/${file.id}`,
    width: file.width ?? null,
    height: file.height ?? null,
    detailUrl: `/api/attachments/${file.id}/detail`,
    fullPreviewKind: fullPreviewKind ?? undefined,
    fullPreviewUrl: fullPreviewKind ? `/api/attachments/${file.id}/preview` : undefined,
    warnHtml: foreign,
  }
}

/** Mesajlardan galeri listesi: mesaj sirasiyla, tekrarlar elenmis. */
export function galleryFilesFromMessages(
  messages: {
    attachments?: GalleryFile[] | null
    tool_cards?: Record<string, unknown>[] | null
  }[],
): GalleryFile[] {
  const files: GalleryFile[] = []
  const seen = new Set<string>()
  const push = (file: unknown) => {
    if (!file || typeof file !== 'object') return
    const candidate = file as GalleryFile
    if (typeof candidate.id !== 'string' || typeof candidate.filename !== 'string') return
    if (seen.has(candidate.id)) return
    // Sesli mesajlar balonun icinde caliyor; resim/belge galerisine girmiyor.
    if (candidate.kind === 'audio') return
    seen.add(candidate.id)
    files.push(candidate)
  }
  for (const message of messages) {
    for (const attachment of message.attachments ?? []) push(attachment)
    for (const card of message.tool_cards ?? []) {
      const outputs = (card as { output_attachments?: unknown }).output_attachments
      if (Array.isArray(outputs)) outputs.forEach(push)
    }
  }
  return files
}

interface GalleryValue {
  /** Ek galeride varsa modal'i o ogeden acar ve `true` doner. */
  openById(id: string): boolean
}

const GalleryContext = createContext<GalleryValue | null>(null)

export function useGallery(): GalleryValue | null {
  return useContext(GalleryContext)
}

export function GalleryProvider({
  files,
  foreign = false,
  children,
}: {
  files: GalleryFile[]
  foreign?: boolean
  children: ReactNode
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const items = useMemo(
    () => files.map((file) => toPreviewItem(file, { foreign })),
    [files, foreign],
  )
  const ids = useMemo(() => files.map((file) => file.id), [files])

  const openById = useCallback(
    (id: string) => {
      const index = ids.indexOf(id)
      if (index < 0) return false
      setOpenIndex(index)
      return true
    },
    [ids],
  )

  const value = useMemo(() => ({ openById }), [openById])

  return (
    <GalleryContext.Provider value={value}>
      {children}
      {openIndex !== null && (
        <AttachmentModal items={items} startIndex={openIndex} onClose={() => setOpenIndex(null)} />
      )}
    </GalleryContext.Provider>
  )
}
